import { useCallback, useState } from 'react';

import { useToast } from '@/components/ui/toast';

import { ApiError, apiErrorReason } from '@/lib/api';
import {
  ACTIVE_COMPANY_INVALID_TEXT,
  ACTIVE_COMPANY_LOST_TEXT,
  NO_COMPANY_TEXT,
} from '@/lib/company-error-text';
import { useApi } from '@/lib/api-context';
import { dispositionFileName, saveBlob } from '@/lib/download';
import { errorAdvice } from '@/lib/error-advice';

// Exportación de un listado (spec 2026-10-14 §3): pide al servidor lo filtrado completo
// en CSV o PDF, lo guarda con el nombre del servidor y avisa el resultado en palabras.

export type ExportFormat = 'csv' | 'pdf';

const LABEL: Record<ExportFormat, string> = { csv: 'CSV', pdf: 'PDF' };
const count = (n: unknown): string => (typeof n === 'number' ? n.toLocaleString('es-CR') : '');

// Motivos del API (`meta.reason`) con su texto: qué pasó y qué hacer (UX-ERR-01). Ninguno
// pide filtrar salvo el tope de filas: hay informes sin filtros (Capacitaciones).
const REASON_TEXT: Record<string, string> = {
  export_busy: 'Hay otras exportaciones en curso. Intenta de nuevo en unos segundos.',
  lms_unavailable:
    'El LMS no respondió y el informe necesita sus datos. Intenta de nuevo en unos minutos.',
  no_active_company: NO_COMPANY_TEXT,
  active_company_forbidden: ACTIVE_COMPANY_LOST_TEXT,
  invalid_active_company_header: ACTIVE_COMPANY_INVALID_TEXT,
  range_too_wide:
    'El rango de fechas es demasiado amplio para este informe. Acórtalo e intenta de nuevo.',
  range_invalid: 'La fecha inicial es posterior a la final. Corrige el rango e intenta de nuevo.',
  limit_exceeded:
    'El informe supera un límite del sistema y no se puede generar. Contacta a soporte.',
};

// Sin motivo conocido, se responde por el status.
const STATUS_TEXT: Record<number, string> = {
  400: 'Algún filtro no es válido. Revísalos e intenta de nuevo.',
  403: 'No tienes permiso para exportar este informe.',
  429: 'Hay demasiadas solicitudes seguidas. Espera un momento e intenta de nuevo.',
  503: 'El servicio no está disponible por ahora. Intenta de nuevo en unos minutos.',
};

// Respuesta del API sin texto propio (500, 404, 409 sin motivo): falla del servidor.
const SERVER_TEXT =
  'No se pudo generar el informe. Intenta de nuevo y, si persiste, contacta a soporte.';

export function exportErrorText(err: unknown): string {
  // Sin respuesta del API (red, contrato): el consejo según la causa.
  if (!(err instanceof ApiError)) {
    return `No se pudo exportar. ${errorAdvice(err)}`;
  }
  const reason = apiErrorReason(err);
  if (reason === 'export_too_large') {
    return `El informe tiene ${count(err.meta?.['total'])} filas y el tope es ${count(err.meta?.['limit'])}. Acota los filtros e intenta de nuevo.`;
  }
  const byReason =
    reason !== undefined && Object.hasOwn(REASON_TEXT, reason) ? REASON_TEXT[reason] : undefined;
  return byReason ?? STATUS_TEXT[err.status] ?? SERVER_TEXT;
}

function slug(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface ExportReport {
  run: (format: ExportFormat) => void;
  /** Formato en curso, o null si no se está exportando. */
  pending: ExportFormat | null;
}

export function useExportReport(path: string, query: string, title: string): ExportReport {
  const api = useApi();
  const { showToast } = useToast();
  const [pending, setPending] = useState<ExportFormat | null>(null);

  const run = useCallback(
    (format: ExportFormat): void => {
      setPending(format);
      const qs = [query, `format=${format}`].filter((p) => p !== '').join('&');
      api.getBlob(`${path}?${qs}`).then(
        ({ blob, headers }) => {
          const name =
            dispositionFileName(headers.get('content-disposition'), format) ??
            `${slug(title)}.${format}`;
          saveBlob(blob, name);
          showToast(`Informe de ${title} descargado en ${LABEL[format]}`);
          setPending(null);
        },
        (err: unknown) => {
          showToast(exportErrorText(err), 'error');
          setPending(null);
        },
      );
    },
    [api, path, query, title, showToast],
  );

  return { run, pending };
}
